// ============================================================================
// Roof Designer · ResultPanel — strukturiertes Ergebnis-Panel
// ----------------------------------------------------------------------------
// Aufbau (von oben nach unten):
//   1. Status-Block: ENTWURF/Nicht freigegeben + Plausibilität + Vollständigkeit
//   2. KPI-Karten: Dachfläche, belegte Fläche, Module, kWp, Packdichte, fehlende
//      Angaben (Karten-Grid statt gedrängter Textliste)
//   3. Befunde & fehlende Angaben
//   4. Sicherheits-/Experten-Hinweise (Statik, Wind/Schnee, keine KI-Freigabe)
//   5. Export-Bereich
// Nichts wird hier gerechnet; alle Werte stammen aus computeDesignerResult.
// canApprove ist strukturell false — dieses Panel kennt keine Freigabe.
// ============================================================================
import type { DesignerState } from "../types.js";
import type { DesignerResult, DesignerSeverity } from "../roof-engine.js";
import { DESIGN_DISCLAIMER } from "../roof-engine.js";

interface Props {
  state: DesignerState;
  result: DesignerResult;
  hasPolygon: boolean;
  onExport: () => void;
  onClearSaved: () => void;
}

const SEV_LABEL: Record<DesignerSeverity, string> = {
  critical: "Kritisch",
  warning: "Warnung",
  advice: "Hinweis",
  info: "Info",
};

const VERDICT: Record<string, { cls: string; label: string }> = {
  ok: { cls: "rd-chip--ok", label: "Plausibel" },
  advice: { cls: "rd-chip--review", label: "Hinweise" },
  warning: { cls: "rd-chip--review", label: "Warnungen" },
  critical: { cls: "rd-chip--missing", label: "Kritisch" },
};

const fmt = (v: number | null, digits = 1): string =>
  v === null ? "—" : v.toLocaleString("de-AT", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** Wie viele der definierten Pflichtangaben sind vollständig? */
export function completenessCount(state: DesignerState): { done: number; total: number } {
  let done = 0;
  const total = 4;
  if (state.polygon.length >= 3 && state.polygonClosed) done++;
  if (state.calibration.confirmed && state.calibration.metersPerPixel) done++;
  if (state.tiltConfirmed) done++;
  if (state.moduleSpec.wp > 0 && state.moduleSpec.widthM > 0 && state.moduleSpec.heightM > 0) done++;
  return { done, total };
}

function KpiCard({ label, value, unit, tone }: { label: string; value: string; unit?: string; tone?: "primary" | "warn" }) {
  return (
    <div className={`rd-kpicard${tone === "primary" ? " rd-kpicard--primary" : ""}${tone === "warn" ? " rd-kpicard--warn" : ""}`}>
      <span className="rd-kpicard__value">
        {value}
        {unit && <span className="rd-kpicard__unit">{unit}</span>}
      </span>
      <span className="rd-kpicard__label">{label}</span>
    </div>
  );
}

export function ResultPanel({ state, result, hasPolygon, onExport, onClearSaved }: Props) {
  const blocked = result.status === "blocked";
  const missingCount = result.missingData.length;
  const { done, total } = completenessCount(state);
  const verdict = result.status === "ok" && result.plausibilityVerdict
    ? VERDICT[result.plausibilityVerdict] ?? null
    : null;

  return (
    <div className="rd-panel" data-testid="rd-result-panel">
      {/* ── 1. Status-Block ── */}
      <div className="rd-statusblock">
        <div className="rd-statusblock__chips">
          <span className="rd-chip rd-chip--draft" title="Dieser Entwurf ist nicht freigegeben. Alle Ergebnisse sind vorläufige Schätzwerte.">
            <span className="rd-chip__dot" />Entwurf
          </span>
          <span className="rd-chip rd-chip--missing" title="canApprove ist strukturell false — KI kann niemals freigeben.">
            <span className="rd-chip__dot" />Nicht freigegeben
          </span>
          {verdict && (
            <span className={`rd-chip ${verdict.cls}`}>
              <span className="rd-chip__dot" />{verdict.label}
            </span>
          )}
          {blocked && (
            <span className="rd-chip rd-chip--blocked">
              <span className="rd-chip__dot" />Belegung gesperrt
            </span>
          )}
        </div>
        <div className="rd-statusblock__meta">
          <span className={`rd-completeness${done === total ? " rd-completeness--ok" : ""}`}>
            {done}/{total} Pflichtangaben
          </span>
          <span className="rd-statusblock__review">Menschliche Prüfung erforderlich</span>
        </div>
      </div>

      {blocked && result.blockedReason && (
        <p className="rd-note rd-note--warn">{result.blockedReason}</p>
      )}

      {/* ── 2. KPI-Karten ── */}
      <div className="rd-kpicards">
        <KpiCard label="Dachfläche" value={fmt(result.projectedAreaM2)} unit="m²" />
        <KpiCard label="belegte Fläche" value={blocked ? "—" : fmt(result.usedAreaM2)} unit="m²" />
        <KpiCard label="Module" value={blocked ? "—" : String(result.moduleCount)} tone="primary" />
        <KpiCard label="Leistung" value={blocked ? "—" : fmt(result.kwp, 2)} unit="kWp" tone="primary" />
        <KpiCard label="Packdichte" value={blocked ? "—" : String(Math.round(result.packingDensity * 100))} unit="%" />
        {missingCount > 0 ? (
          <KpiCard label="fehlende Angaben" value={String(missingCount)} tone="warn" />
        ) : (
          <KpiCard label="fehlende Angaben" value="0" />
        )}
      </div>

      {result.status === "ok" && result.plausibilitySummary && (
        <p className="rd-note">{result.plausibilitySummary}</p>
      )}

      {/* ── 3. Befunde & fehlende Angaben ── */}
      {(result.findings.length > 0 || missingCount > 0) && (
        <div className="rd-section">
          <span className="rd-section__title">Befunde & offene Angaben</span>
          <ul className="rd-findings">
            {result.missingData.map((m, i) => (
              <li key={`m-${i}`} className="rd-finding rd-finding--warning">
                <span className="rd-finding__sev">Fehlt</span>
                <span>{m}</span>
              </li>
            ))}
            {result.findings.map((f, i) => (
              <li key={`${f.code}-${i}`} className={`rd-finding rd-finding--${f.severity}`}>
                <span className="rd-finding__sev">{SEV_LABEL[f.severity]}</span>
                <span>{f.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── 4. Sicherheits-Hinweise ── */}
      <div className="rd-section">
        <span className="rd-section__title">Sicherheit & Grenzen</span>
        <ul className="rd-safety">
          <li><strong>Kein Statiknachweis</strong> — Auflasten, Verankerung, Windlast durch befugte Fachkraft.</li>
          <li><strong>Keine Wind-/Schneelastfreigabe</strong> — ÖNORM EN 1991 standortspezifisch durch Fachbetrieb.</li>
          <li><strong>Keine finale Modulbelegung</strong> — Belegung ist Schätzwert, keine Montageplanung.</li>
          <li><strong>Keine KI-Freigabe</strong> — canApprove strukturell false; Endabnahme durch Planer/Elektroinstallateur.</li>
        </ul>
        <p className="rd-note rd-note--fine">{DESIGN_DISCLAIMER}</p>
      </div>

      {/* ── 5. Export ── */}
      <div className="rd-section rd-export">
        <button
          className="rd-export__btn"
          onClick={onExport}
          disabled={!hasPolygon}
          title={hasPolygon ? "Entwurf als JSON exportieren" : "Erst Dachfläche festlegen"}
        >
          ⤓ Entwurf exportieren (JSON)
        </button>
        <button
          className="rd-export__clear"
          onClick={onClearSaved}
          title="Löscht den lokal gespeicherten Zeichenstand für dieses Projekt"
        >
          Lokalen Stand löschen
        </button>
      </div>
    </div>
  );
}
